import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bell } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toSaudiDateTime } from '../utils/timeUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationBell = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [researcherProfile, setResearcherProfile] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    
    fetchResearcherProfile();
  }, [user]);

  useEffect(() => {
    if (researcherProfile) {
      fetchUnreadCount();
      fetchNotifications();
      
      // Refresh every 30 seconds
      const interval = setInterval(() => {
        fetchUnreadCount();
        fetchNotifications();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [researcherProfile]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchResearcherProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/researchers/`);
      if (response.ok) {
        const profiles = await response.json();
        const myProfile = profiles.find(p => p.email === user.email);
        if (myProfile) {
          setResearcherProfile(myProfile);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchUnreadCount = async () => {
    if (!researcherProfile) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/connections/unread-count/${researcherProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    if (!researcherProfile) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/connections/notifications/${researcherProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        // Only show the latest 5 unread notifications in dropdown
        const unread = data.filter(n => !n.read).slice(0, 5);
        setNotifications(unread);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await fetch(`${BACKEND_URL}/api/connections/notifications/${notificationId}/read`, {
        method: 'PUT'
      });
      fetchUnreadCount();
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleViewAll = () => {
    setShowDropdown(false);
    navigate('/dashboard');
  };

  if (!user || !researcherProfile) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        className="relative"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs">
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <p className="text-xs text-gray-500">{unreadCount} unread</p>
            )}
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">No new notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      handleMarkAsRead(notif.id);
                      if (notif.link) {
                        navigate(notif.link);
                        setShowDropdown(false);
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {notif.type === 'connection_request' && (
                          <div className="h-2 w-2 rounded-full bg-blue-500" />
                        )}
                        {notif.type === 'message' && (
                          <div className="h-2 w-2 rounded-full bg-green-500" />
                        )}
                        {notif.type === 'connection_accepted' && (
                          <div className="h-2 w-2 rounded-full bg-cyan-500" />
                        )}
                        {notif.type === 'project_completed' && (
                          <div className="h-2 w-2 rounded-full bg-green-600" />
                        )}
                        {notif.type === 'task_deadline_approaching' && (
                          <div className="h-2 w-2 rounded-full bg-amber-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{notif.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(notif.created_at).toLocaleString('en-US', {
                            timeZone: 'Asia/Riyadh',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
              onClick={handleViewAll}
            >
              View All in Dashboard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
